import {
  createFileRoute,
  Navigate,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { z } from 'zod';
import { useQuery } from '@apollo/client';
import PageLoader from 'src/components/ui/PageLoader';
import RequestPreviewContainer from 'src/components/creator/RequestPreviewContainer';
import REQUEST_QUERY from 'src/graphql/query/order-request.graphql';
import PageTitle from 'src/components/creator/PageTitle';
import Button from 'src/components/ui/Button';
import useUser from 'src/hooks/useUser';
import { canSeeNetPrice } from 'src/utils/order-requests';

const OrderRequestPage = () => {
  const { requestId } = Route.useParams();
  const navigate = useNavigate();
  const { user, loading: userLoading } = useUser();
  const { data, loading, error } = useQuery(REQUEST_QUERY, {
    variables: { requestId },
  });

  // Same rule as the requests table: hidden in the list means hidden here too.
  const showNetPrice = canSeeNetPrice({
    clientType: user?.clientType,
    isFacultyCustomer: user?.isFacultyCustomer,
  });

  if (loading || userLoading) return <PageLoader />;
  if (error) return <Navigate to="/requests" />;

  return (
    <>
      <PageTitle title={`Order Request: ${data.request.requestId}`} />
      <div className="flex items-center gap-2 px-4 pt-6 pb-3">
        <Button
          variant="secondary"
          onPress={() => navigate({ to: '/requests' })}
          className="ml-auto"
        >
          Dashboard
        </Button>
      </div>
      <div className="relative overflow-y-auto bg-surface-alt rounded-xl shadow-3 mt-4 px-6 pt-11 pb-20 min-h-0 flex-1">
        <RequestPreviewContainer
          data={data.request}
          className="max-w-[800px] mx-auto"
          showNetPrice={showNetPrice}
        />
      </div>
    </>
  );
};

const paramsSchema = z.object({
  requestId: z.coerce.number().int().positive(),
});

export const Route = createFileRoute(
  '/_authenticated/_creator/requests/$requestId/'
)({
  beforeLoad: async ({ params }) => {
    const result = paramsSchema.safeParse(params);
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (!result.success) throw redirect({ to: '/requests' });
  },
  component: OrderRequestPage,
});
